import React, { useEffect, useState, useContext } from "react";
import { useAlert } from 'react-alert';
import { UserContext } from "../../UserContext";
import { useNavigate } from "react-router-dom";

import CenteredPageContainer from "../components/layout_components/CenteredPageContainer";
import PlaceholderCardLayout from "../components/layout_components/PlaceholderCardLayout";
import Button from "../components/common_components/Button";
import { CustomConnectButton } from "../components/common_components/CustomConnectButton";
import WalletIcon from "../../../../../assets/images/user_app/wallet_icon.svg";
import CenteredLoadingIcon from "../components/common_components/CenteredLoadingIcon"
import WalletsTable from "../components/wallet_components/WalletsTable";
import { useDisconnect } from 'wagmi'
import { useSignMessage, useAccount } from "wagmi";

import ClipLoader from "react-spinners/ClipLoader";
import { useConnectModal } from '@rainbow-me/rainbowkit';

// API Calls
import UserWalletAPI from "../api/api_calls/UserWalletAPI";



function WalletsPage(props) {
    const { user, setUser, refreshUser } = useContext(UserContext);
    const alert = useAlert();
    const navigate = useNavigate();

    // Wallets State
    const { disconnect } = useDisconnect()
    const { address } = useAccount()
    const [messageToSign, setMessageToSign] = useState("Despark is having a server error. Signing this is harmless, but won't log you in. Please try again later.");
    const { data, isError, isLoading, isPending, isSuccess, signMessage } = useSignMessage();
    const { connectModalOpen } = useConnectModal();



    // --------------------------------------------
    // First, we fetch the user's wallets
    // --------------------------------------------
    const [wallets, setWallets] = useState([]);
    const [verifiedWallets, setVerifiedWallets] = useState([]);
    const [refreshWallets, setRefreshWallets] = useState(0);
    const [loadComplete, setLoadComplete] = useState(false);
    const walletExists = address && wallets.length > 0 && wallets.find(wallet => wallet.address.toLowerCase() == address.toLowerCase())
    const walletConnectedAndVerified = walletExists && walletExists.signature_verified // If the wallet exists and is verified
    const walletUnverified = walletExists && !walletExists.signature_verified // If the wallet exists but isn't verified
    console.log("Address: ", address)

    const getUserWallets = async () => {
        const data = await UserWalletAPI.index();

        if (data.success) {
            const verifiedWallets = data.user_wallets.filter(wallet => wallet.signature_verified)
            setWallets(data.user_wallets)
            setVerifiedWallets(verifiedWallets)
            setLoadComplete(true);
        } else {
            alert.show("Unable to load wallets. Try again later", { type: 'error' })
        }
    }

    useEffect(() => {
        document.title = 'Wallets | Despark';
    }, []);

    useEffect(() => {
        getUserWallets();
    }, [refreshWallets]);

    function UpdateWallets() {
        setRefreshWallets(refreshWallets + 1)
    }

    async function UpdateWalletsAndDisconnect() {
        await getUserWallets()
        disconnectWallet()
    }

    async function disconnectWallet() {
        disconnect()
        if (connectModalOpen) {
            connectModalOpen()
        }
    }

    // If the user is connected, add their wallet to the list of wallets
    const AddWallet = async (address) => {
        console.log("Adding wallet")
        const data = await UserWalletAPI.create({ address: address, handleRedirect: navigate })

        if (data["success"]) {
            UpdateWallets()
        } else {
            alert.error(data["error"], { timeout: 10000 })
            disconnectWallet()
        }
    }

    // If the user is connected, get the nonce and prepare the signature
    const GetNonceAndPrepareSignature = async (address) => {
        console.log("Getting nonce")
        const wallet = address && wallets.find(wallet => wallet.address.toLowerCase() == address.toLowerCase())
        const result = await UserWalletAPI.getNonce({ walletId: wallet.id, navigate });

        if (result.success) {
            // Update the message to have the correct nonce.
            setMessageToSign(result.signature_message)
        } else {
            // Display an error message
            alert.error("Something went wrong - our servers can't currently handle wallet signatures. Please try again later or sign up using email. ")
        }
    }

    // Prompt the user to sign their wallet
    function PromptSignMessage() {
        if (messageToSign.startsWith("Logging")) {
            signMessage({ message: messageToSign });
        }
    }

    // --------------------------------------------
    // --------------------------------------------
    // -----------Disconnect Wallets---------------
    // --------------------------------------------
    // --------------------------------------------

    // On load, disconnect any wallets using rainbowkit and wagmi
    useEffect(() => {
        disconnectWallet()
    }, []);

    // --------------------------------------------
    // --------------------------------------------
    // -----------Signing Logic---------------
    // --------------------------------------------
    // --------------------------------------------

    useEffect(() => {
        // 1. If !address, do nothing. 
        // 2. If address exists (!!address), check if it's in our wallets list and whether it's verified. 
        // Once we're loaded, check if the wallet is in our list of wallets.
        if (!!address && loadComplete) {
            // Check if the wallet is in our list of wallets
            if (!walletExists) {
                // Wallet doesn't exist. Add it to our list of wallets for this user.
                AddWallet(address)
            } else if (walletUnverified) {
                /// Wallet exists but isn't verified. Prompt a signature.
                GetNonceAndPrepareSignature(address)
            } else {
                // Wallet exists and is verified. Do nothing.
            }
        }
    }, [address, loadComplete, wallets]);



    // Upon successful signature, verify the signature.
    useEffect(() => {
        // They've successfully signed.
        if (isSuccess) {
            const VerifySignature = async () => {
                console.log("Verifying signature")
                const wallet = address && wallets.find(wallet => wallet.address.toLowerCase() == address.toLowerCase())
                const result = await UserWalletAPI.verify({ walletId: wallet.id, address: address, signature_data: data, handleRedirect: navigate })

                if (result.success) {
                    // Update the wallets.
                    UpdateWalletsAndDisconnect()

                    if (connectModalOpen) {
                        connectModalOpen()
                    }
                    // Longer timeout, since the modal pops up and distracts the user.
                    alert.success("Wallet verified. Updating mission approvals, this may take a couple of minutes.", { timeout: 10000 })
                } else {
                    // Display an error message
                    alert.error("Something went wrong. Please try again later. ")
                }
            }

            VerifySignature()
        }
    }, [isSuccess]);

    // --------------------------------------------
    // --------------------------------------------
    // ----------------Render Logic---------------
    // --------------------------------------------
    // --------------------------------------------

    // Logic for this page:
    // If there are NO wallets, show the AddWalletCard
    // If there are wallets, show the WalletsTable
    // On load, disconnect any wallets
    // Once the user connects a wallet, they are shown the AddWalletsCard
    // They are prompted to sign their wallet
    // Once they sign their wallet, or disconnect their wallet, they go back to default 
    // (either table or Add depending on number of wallets)
    return (
        <CenteredPageContainer>
            {!loadComplete ? (
                <CenteredLoadingIcon />
            ) :
                <>
                    {verifiedWallets.length === 0 || address ? (
                        <AddWalletCard
                            address={address}
                            walletUnverified={walletUnverified}
                            promptSignMessage={PromptSignMessage}
                            isPending={isPending}
                        />
                    ) : (
                        <WalletsTable wallets={verifiedWallets} />
                    )
                    }
                </>
            }
        </CenteredPageContainer>
    )
}



function AddWalletCard(props) {
    console.log("AddWalletCard")
    console.log("Wallet Unverified: ", props.walletUnverified)
    console.log("Address: ", props.address)
    console.log("Is Pending: ", props.isPending)
    return (
        <PlaceholderCardLayout>
            <img src={WalletIcon} classalt="Wallet" />
            <div className="placeholder-card-text-content-container">
                <h1 className="centered-h1-card-text">Add wallets to qualify for missions</h1>
                <div className="centered-text body-medium grey-400-text">
                    Connect and verify your wallets to qualify for missions.
                </div>
            </div>
            <div className="full-width-button-container">
                <CustomConnectButton size="large" />
            </div>
            {props.address && props.walletUnverified &&
                (props.isPending ? (
                    <div className="full-width-button-container">
                        <Button size="large" color="white" onClick={() => {
                            // Do nothing
                        }}>
                            Waiting for signature...
                            <ClipLoader color="#000000" size={26} />
                        </Button>
                    </div>
                ) : (
                    <div className="full-width-button-container">
                        <Button size="large" color="primary" fullWidth={true} onClick={() => {
                            props.promptSignMessage()
                        }}>
                            Sign & Verify
                        </Button>
                    </div>
                ))}
            {props.address && !props.walletUnverified &&
                <div className="wallets-table-info-box body-small">
                    Wallet already verified. Disconnect and reconnect to add a new wallet.
                </div>

            }
        </PlaceholderCardLayout>
    )
}


export default WalletsPage